import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  MenuList,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import {
  ChemicalField,
  ChemicalFieldGroup,
  ChemicalFields,
} from "../../../types/chemicals";
import { ICropType, ICropTypeChemicalField } from "../../../types/cropType";
import GeneralPopoverWrapper from "../Common/GeneralPopoverWrapper";

const shakeFieldName = (name?: string) =>
  (name || "")
    .replace("F ", "")
    .replace("W ", "")
    .replace("%DM", "")
    ?.split("%")?.[0]
    ?.split("/")?.[0]
    ?.split(":Hr")?.[0]
    ?.split(":hr")?.[0]
    .toLowerCase() || "";

const fieldSearchKeys = (field: {
  fieldName?: string;
  aliases?: string[];
}) =>
  [field.fieldName, ...(field.aliases || [])].filter(
    (name): name is string => !!name
  );

const fieldMatchesQuery = (
  field: { fieldName?: string; aliases?: string[] },
  query: string
) => {
  if (!query) return true;
  return fieldSearchKeys(field).some(
    (name) =>
      name.toLowerCase().includes(query) ||
      shakeFieldName(name).includes(query)
  );
};

const shortestMatchLength = (
  field: { fieldName?: string; aliases?: string[] },
  query: string
) => {
  const matching = fieldSearchKeys(field).filter(
    (name) =>
      !query ||
      name.toLowerCase().includes(query) ||
      shakeFieldName(name).includes(query)
  );
  if (!matching.length) return field.fieldName?.length || 0;
  return Math.min(...matching.map((name) => name.length));
};

export default function CropTypeChemicalFieldsPopoverContent({
  chemicalFields,
  onChange,
  cropTypes,
  currentCropTypeId,
}: {
  chemicalFields: ICropTypeChemicalField[];
  onChange: (fields: ICropTypeChemicalField[]) => void;
  cropTypes?: ICropType[];
  currentCropTypeId?: string;
}) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [autoCompleteKey, setAutoCompleteKey] = React.useState(0);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [autoCompleteKey]);

  const assignedNames = React.useMemo(() => {
    const names = new Set<string>();
    (chemicalFields || []).forEach((field) => {
      fieldSearchKeys(field).forEach((name) => {
        names.add(name.toLowerCase());
        names.add(shakeFieldName(name));
      });
    });
    ChemicalFields.forEach((field) => {
      const keys = fieldSearchKeys(field);
      if (
        keys.some(
          (name) =>
            names.has(name.toLowerCase()) || names.has(shakeFieldName(name))
        )
      ) {
        keys.forEach((name) => {
          names.add(name.toLowerCase());
          names.add(shakeFieldName(name));
        });
      }
    });
    return names;
  }, [chemicalFields]);

  const isFieldAssigned = (field: ChemicalField) =>
    fieldSearchKeys(field).some(
      (name) =>
        assignedNames.has(name.toLowerCase()) ||
        assignedNames.has(shakeFieldName(name))
    );

  const options = React.useMemo(
    () =>
      ChemicalFields.filter(
        (field) =>
          (field.group === ChemicalFieldGroup.FTI ||
            field.group === ChemicalFieldGroup.NAG) &&
          !isFieldAssigned(field)
      ).sort((a, b) => {
        if (a.group === b.group) {
          return (a.fieldName || "").localeCompare(b.fieldName || "");
        }
        return a.group === ChemicalFieldGroup.FTI ? -1 : 1;
      }),
    [assignedNames]
  );

  const ftiFields = (chemicalFields || []).filter(
    (field) => field.group === ChemicalFieldGroup.FTI
  );
  const nagFields = (chemicalFields || []).filter(
    (field) => field.group === ChemicalFieldGroup.NAG
  );

  const addField = (field: ChemicalField) => {
    if (!field.fieldName) return;
    if (isFieldAssigned(field)) return;
    onChange([
      ...(chemicalFields || []),
      { ...field, group: field.group },
    ]);
  };

  const removeField = (fieldName: string) => {
    onChange(
      (chemicalFields || []).filter((field) => field.fieldName !== fieldName)
    );
  };

  const clearGroup = (group: ChemicalFieldGroup) => {
    onChange(
      (chemicalFields || []).filter((field) => field.group !== group)
    );
  };

  const addAllGroup = (group: ChemicalFieldGroup) => {
    const toAdd = ChemicalFields.filter(
      (field) =>
        field.group === group && !isFieldAssigned(field)
    );
    if (!toAdd.length) return;
    onChange([
      ...(chemicalFields || []),
      ...toAdd.map((field) => ({ ...field, group: field.group })),
    ]);
  };

  const copyableCropTypes = React.useMemo(
    () =>
      [...(cropTypes || [])]
        .filter((cropType) => cropType._id !== currentCropTypeId)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [cropTypes, currentCropTypeId]
  );

  const copyFromCropType = (cropType: ICropType) => {
    onChange(
      (cropType.chemicalFields || []).map((field) => ({ ...field }))
    );
  };

  const renderSection = (
    title: string,
    fields: ICropTypeChemicalField[],
    group: ChemicalFieldGroup
  ) => (
    <Box sx={{ minWidth: "200px", display: "inline-block", verticalAlign: "top", mr: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={fields.length}
          color={fields.length > 0 ? "warning" : "default"}
          sx={{
            height: 18,
            minWidth: 18,
            "& .MuiChip-label": { px: 0.75, fontSize: 11 },
          }}
        />
        {fields.length ? (
          <Button
            endIcon={<ClearIcon />}
            variant="text"
            size="small"
            color="error"
            onClick={() => clearGroup(group)}
            sx={{ ml: 1, minWidth: "unset", px: 0.5 }}
          >
            {t("Clear all")}
          </Button>
        ) : (
          <Button
            endIcon={<AddIcon />}
            variant="text"
            size="small"
            onClick={() => addAllGroup(group)}
            sx={{ ml: 1, minWidth: "unset", px: 0.5 }}
          >
            {t("Add all")}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.75,
          maxHeight: 280,
          overflow: "auto",
          pr: 0.5,
        }}
      >
        {fields.map((field) => (
          <Chip
            key={field.fieldName}
            variant="outlined"
            label={field.fieldName}
            onDelete={() => removeField(field.fieldName)}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: "90vw", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Autocomplete
        key={autoCompleteKey}
        sx={{ flex: 1, minWidth: 0 }}
        autoHighlight
        blurOnSelect={false}
        size="small"
        fullWidth
        options={options}
        groupBy={(option) => option.group || ""}
        renderGroup={(params) => (
          <Box key={params.key}>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "warning.main",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#2c3a55" : "#f4f4f4",
                borderBottom: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark" ? "#3d4d6b" : "#e0e0e0"
                  }`,
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {params.group} 
            </Box>
            {params.children}
          </Box>
        )}
        value={null}
        onChange={(_e, value) => {
          if (value) {
            addField(value);
            setAutoCompleteKey(autoCompleteKey + 1);
          }
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.fieldName}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {option.fieldName}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {(option.aliases || [])
                .filter(
                  (alias) =>
                    alias &&
                    alias.toLowerCase() !== option.fieldName?.toLowerCase()
                )
                .join(", ")}
            </Typography>
          </Box>
        )}
        getOptionLabel={(option) =>
          [option.fieldName, ...(option.aliases || [])]
            .filter(Boolean)
            .join(" ")
        }
        filterOptions={(opts, state) => {
          const query = (state.inputValue || "").toLowerCase().trim();
          const filtered = query
            ? opts.filter((option) => fieldMatchesQuery(option, query))
            : opts;
          return [...filtered].sort((a, b) => {
            const groupRank = (group?: string) => {
              if (group === ChemicalFieldGroup.FTI) return 0;
              if (group === ChemicalFieldGroup.NAG) return 1;
              return 2;
            };
            const byGroup = groupRank(a.group) - groupRank(b.group);
            if (byGroup !== 0) return byGroup;
            if (query) {
              return (
                shortestMatchLength(a, query) - shortestMatchLength(b, query)
              );
            }
            return (a.fieldName || "").localeCompare(b.fieldName || "");
          });
        }}
        isOptionEqualToValue={(option, value) =>
          option.fieldName === value.fieldName
        }
        componentsProps={{
          popper: {
            disablePortal: true,
            placement: "bottom-start",
            sx: { zIndex: 1500 },
          },
        }}
        ListboxProps={{
          sx: { maxHeight: 240 },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Add Chemical Field")}
            inputRef={inputRef}
          />
        )}
      />
      <GeneralPopoverWrapper
        disablePortal
        stopPropagationOnClick
        closeOnClickContent
        disablePop={!copyableCropTypes.length}
        triggerElement={
          <Button
            variant="outlined"
            size="small"
            disabled={!copyableCropTypes.length}
            sx={{ height: 38, whiteSpace: "nowrap", mt: "1px" }}
          >
            {t("Copy From ...")}
          </Button>
        }
        popoverContent={
          <MenuList sx={{ minWidth: 220, maxHeight: 320, overflow: "auto" }}>
            {copyableCropTypes.map((cropType) => {
              const ftiCount = (cropType.chemicalFields || []).filter(
                (field) => field.group === ChemicalFieldGroup.FTI
              ).length;
              const nagCount = (cropType.chemicalFields || []).filter(
                (field) => field.group === ChemicalFieldGroup.NAG
              ).length;
              return (
                <MenuItem
                  key={cropType._id}
                  onClick={() => copyFromCropType(cropType)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2">{cropType.name}</Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={`FTI ${ftiCount}`}
                        color={ftiCount > 0 ? "warning" : "default"}
                        sx={{
                          height: 18,
                          "& .MuiChip-label": { px: 0.75, fontSize: 11 },
                        }}
                      />
                      <Chip
                        size="small"
                        label={`NAG ${nagCount}`}
                        color={nagCount > 0 ? "warning" : "default"}
                        sx={{
                          height: 18,
                          "& .MuiChip-label": { px: 0.75, fontSize: 11 },
                        }}
                      />
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
          </MenuList>
        }
      />
      </Box>
      <Box sx={{  mt: 2}}>
        {renderSection("FTI", ftiFields, ChemicalFieldGroup.FTI)}
        {renderSection("NAG", nagFields, ChemicalFieldGroup.NAG)}
      </Box>
    </Box>
  );
}
